import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hooks, media } from '@lifesize/clients.sdk';
import { currentUserSelectors, environmentSelectors } from '@lifesize/nucleus';
import _get from 'lodash/get';
import Popout from 'components/Popout/Popout';
import { FIRST_TIME_USER_SETTING } from 'constants/UserSettings';
import Header from 'components/Header/Header';
import SideNav from 'containers/SideNav/SideNavContainer';
import GetPermission from 'components/GetPermission/GetPermission';
import Onboarding from 'components/Onboarding/Onboarding';
import { popoutPresentationSelector } from 'selectors/call';
import { isStartingCall } from 'utils/call-utils';
import { logger } from 'utils/logger';
import { isChrome } from 'utils/browser-utils';
import { getDevicesByKind } from 'utils/deviceUtils';
import { acquireWebcam } from 'actions/media';

import 'styles/core.module.scss';
import classes from './CoreLayout.module.scss';

import { hidePermission, showOnboarding } from 'actions/Onboarding';
import Logger from 'js-logger';

logger.info(`Webapp version: ${process.env.REACT_APP_VERSION}`);

interface Props {
  children: React.ReactNode;
}

const CoreLayout = (props: Props) => {
  const dispatch = useDispatch();

  const callState = hooks.useCallState();
  const devices = hooks.useDevices();
  const hasAudio = !!useMemo(() => getDevicesByKind(devices, 'audioinput'), [devices])?.length;
  const hasVideo = !!useMemo(() => getDevicesByKind(devices, 'videoinput'), [devices])?.length;
  const firstTimeUser = useSelector((state) =>
    _get(currentUserSelectors.myInfo(state), ['userSettings', FIRST_TIME_USER_SETTING])
  );
  const isDesktop = useSelector((state) => environmentSelectors.platform(state).isDesktop);
  const modalIsOpen = useSelector((state) => !!_get(state, 'app.modal.visibleComponent'));
  const isFullScreenModalVisible = isStartingCall(callState) || modalIsOpen;
  const isPopoutOpen = useSelector((state) => popoutPresentationSelector(state)); // IMPORTANT: for the guest join flow, CoreLayout is not used
  const partialModal = useSelector((state) => _get(state, 'app.modal.partialModal'));
  const showPermissions = useSelector((state) => _get(state, 'app.onboarding.showPermissions'));
  const showPermissionPage = showPermissions && !isDesktop;
  const showOnBoardingComponent =
    useSelector((state) => !_get(state, 'app.onboarding.disableOnboarding')) && !isDesktop;
  const { isCheckedIn } = hooks.useUserInfo();

  useEffect(() => {
    // componentDidMount
    if (firstTimeUser) {
      dispatch(showOnboarding());
    }

    // initiate webcam for permissions / devices if not in desktop
    if (isCheckedIn && !media.isAcquiring() && !isDesktop) {
      //@ts-ignore
      Logger.info('CoreLayout: Acquire Webcam');
      dispatch(acquireWebcam());
    }
    // note: an empty dependency array indicates that the effect is only run once (equivalent to componentDidMount() & [componentWillUnmount()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasVideo && hasAudio && showPermissions) {
      dispatch(hidePermission());
    }
  }, [dispatch, hasAudio, hasVideo, showPermissions]);

  return (
    <div>
      {showPermissionPage && isChrome() ? <GetPermission /> : null}
      {!showPermissionPage && showOnBoardingComponent && <Onboarding />}
      <div className={classes.vertFlexContainer}>
        <Header />
        <div
          className={
            isFullScreenModalVisible && !partialModal ? classes.horzFlexContainerBlur : classes.horzFlexContainer
          }
        >
          <SideNav />
          <div className={classes.mainContainer}>{props.children}</div>
        </div>
      </div>
      {isPopoutOpen && <Popout />}
    </div>
  );
};

export default CoreLayout;
